import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '@guryou/colors';
import { PaymentSettings, PricingModel } from '@guryou/core';
import { Block, Form, Spinner, ValidationSummary } from '@guryou/html-components';
import { createSubscriptionAsAnonymous, getSubscriptionPrice, stateIsNotReady, useEffectOnSuccess } from '@guryou/redux';
import { isValidEmail, printDate, printPrice, printTime } from '@guryou/utils';
import { useTheme } from '@material-ui/core/styles';
import WidgetContactForm from './WidgetContactForm';
import { WidgetContext } from './WidgetContext';

const WidgetSubscription = ({ gyClass, closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { subscriptionPrice } = useSelector(state => state.widget);
  const { selectedSessions, setSessionActiveStep, error, acceptPayments, selsectedOfferForCourse, fontF, fontS } = useContext(WidgetContext);

  useEffect(() => {
    dispatch(getSubscriptionPrice({ classId: +selsectedOfferForCourse, sessions: selectedSessions }));
  }, [dispatch, selsectedOfferForCourse, selectedSessions]);

  useEffectOnSuccess([state => state.subscriptions.create], () => {
    setSessionActiveStep(s => s - 1);
  });

  if (stateIsNotReady(subscriptionPrice)) {
    return <Spinner />;
  }

  const onSubmit = data => {
    if (!isValidEmail(data.email)) {
      return;
    }
    dispatch(createSubscriptionAsAnonymous({ ...data, classId: +selsectedOfferForCourse, sessions: selectedSessions }));
    closeModal();
  };

  let errorContent = null;
  if (error) {
    if (Array.isArray(error)) {
      errorContent = <ValidationSummary marginLeft={0} errorMessage={t(`VALIDATIONS.${error[0].message}`)} />;
    }
  }

  return (
    <Block>
      <Form parserVersion={2} onSubmit={onSubmit} actions={false}>
        <div class="relative overflow-x-auto">
          <ol class="relative text-gray-500 border-s border-gray-200 ml-4 pt-6">
            <li class="mb-10 ms-6">
              <span
                class={`absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:bg-[${colors.mandy}]`}
              >
                <svg
                  class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill-rule="evenodd"
                    d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-2 9a4 4 0 0 0-4 4v1a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1a4 4 0 0 0-4-4H6Zm7.25-2.095c.478-.86.75-1.85.75-2.905a5.973 5.973 0 0 0-.75-2.906 4 4 0 1 1 0 5.811ZM15.466 20c.34-.588.535-1.271.535-2v-1a5.978 5.978 0 0 0-1.528-4H18a4 4 0 0 1 4 4v1a2 2 0 0 1-2 2h-4.535Z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
              <h3 class="font-medium leading-tight">{t('BOOK_WIDGET.sessionsSelected')}</h3>
              <p class="text-sm">{t('CENTERS_PAGE.seeSessions')}</p>
              <div className="w-full overflow-x-auto overflow-y-auto max-h-[160px] pt-4">
                <table class="w-full text-sm text-left rtl:text-right text-gray-500 ">
                  <thead class={`text-xs text-white uppercase bg-[${colors.mandy}]`}>
                    <tr>
                      <th scope="col" class="px-6 py-3">
                        {t('CALENDAR.date')}
                      </th>
                      <th scope="col" class="px-6 py-3">
                        {t('SERVICES.startTime')}
                      </th>
                      <th scope="col" class="px-6 py-3">
                        {t('SERVICES.endTime')}
                      </th>
                      {gyClass.model !== PricingModel.total && (
                        <th scope="col" class="px-6 py-3">
                          {t('GENERAL.price')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {gyClass.sessions
                      .filter(session => selectedSessions.includes(session.id))
                      .map(x => (
                        <tr class="bg-white border-b">
                          <th scope="row" class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                            {printDate(x.startTime)}
                          </th>
                          <td class="px-6 py-4">{printTime(x.startTime)}</td>
                          <td class="px-6 py-4">{printTime(x.endTime)}</td>
                          {gyClass.model !== PricingModel.total && <td class="px-6 py-4">{printPrice(x.price)}</td>}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div class="space-y-4 rounded-lg border border-gray-100 bg-gray-50 p-6 mt-4">
                <div class="space-y-2">
                  <dl class="flex items-center justify-between gap-4">
                    <dt class="text-base font-normal text-gray-500">{t('GENERAL.duration')}</dt>
                    <dd class="text-base font-medium text-gray-900">{gyClass.duration}</dd>
                  </dl>
                  <dl class="flex items-center justify-between gap-4">
                    <dt class="text-base font-normal text-gray-500">{t('BOOK_WIDGET.pricingModel')}</dt>
                    <dd class="text-base font-medium">{gyClass.model}</dd>
                  </dl>
                </div>
                <dl class="flex items-center justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-700">
                  <dt class="text-base font-bold text-gray-900">{t('BOOK_WIDGET.totalCost')}</dt>
                  <dd class="text-base font-bold text-gray-900">{printPrice(subscriptionPrice.data.price)}</dd>
                </dl>
              </div>
            </li>
            <li class="mb-10 ms-6">
              <span
                class={`absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:bg-[${colors.mandy}]`}
              >
                <svg
                  class="w-3.5 h-3.5 text-gray-800 dark:text-white"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 16"
                >
                  <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z" />
                </svg>
              </span>
              <h3 class="font-medium leading-tight">{t('SERVICES.personalData')}</h3>
              <p class="text-sm">{t('SERVICES.personalDataStepDescription')}</p>

              <div class="mt-6 grow sm:mt-8 lg:mt-4">
                <div>
                  <WidgetContactForm />
                </div>
              </div>
            </li>
          </ol>
        </div>
        <div class="flex justify-end mt-6 gap-2">
          <button
            type="button"
            name="onlyBook"
            value="true"
            onClick={() => setSessionActiveStep(s => s - 1)}
            class="max-w-[160px] text-gray-900 bg-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
          >
            {t('GENERAL.goBack')}
          </button>
          {[PaymentSettings.NoPayments, PaymentSettings.Optional].includes(acceptPayments) && (
            <button
              name="onlyBook"
              value="true"
              type="submit"
              style={{ backgroundColor: colors.mandy }}
              class="max-w-[160px] text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
            >
              {t('NEWSLETTER.subscribe')} {printPrice(subscriptionPrice.data.priceWithoutDiscount)}
            </button>
          )}
          {[PaymentSettings.OnlineOnly, PaymentSettings.Optional].includes(acceptPayments) && (
            <button
              type="submit"
              style={{ backgroundColor: colors.mandy }}
              class="max-w-[160px] text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-200 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-red-100 font-medium rounded-lg text-sm px-5 py-2.5 inline-flex justify-center w-full text-center"
            >
              {`${t('GENERAL.payNow')} ${printPrice(subscriptionPrice.data.price)}`}
            </button>
          )}
        </div>
        <Block>{errorContent}</Block>
      </Form>
    </Block>
  );
};

export default WidgetSubscription;
